import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  TextField,
  TextFieldProps
} from '@mui/material'
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FuseOption {
  label: string
  value: number
}

export interface FuseAutocompleteProps extends Omit<TextFieldProps, 'children' | 'onChange'> {
  value: number | undefined
  onChange: (value: number | undefined) => void
}

export default function FuseAutocomplete({ value, onChange, ...props }: FuseAutocompleteProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The options.
   */
  const options: FuseOption[] = [
    { label: '0A', value: 0 },
    { label: '16A', value: 16 },
    { label: '32A', value: 32 },
    { label: '63A', value: 63 },
    { label: '125A', value: 125 },
    { label: '200A', value: 200 },
    { label: '250A', value: 250 },
    { label: '300A', value: 300 },
    { label: '400A', value: 400 },
    { label: '500A', value: 500 },
    { label: '600A', value: 600 },
    { label: '800A', value: 800 },
    { label: '1000A', value: 1000 },
    { label: '1200A', value: 1200 }
  ]

  const [inputValue, setInputValue] = useState<string | undefined>(
    value === undefined ? undefined : `${value}`
  )

  const autocompleteValue: string | FuseOption | null =
    (options.find((option) => option.value === value) ?? value === undefined) ? null : `${value}A`

  const handleChange = (
    _event: SyntheticEvent,
    option: string | FuseOption | null,
    _reason: AutocompleteChangeReason
  ) => {
    if (typeof option === 'string') {
      if (option !== '' && !isNaN(parseInt(option))) {
        onChange(parseInt(option))
      }
    } else if (option !== null) {
      onChange(option.value)
    }
  }

  const handleInputChange = (
    _event: SyntheticEvent,
    _value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // Handle the event when the user clears the input
    // using the clear button.
    if (reason === 'clear') {
      onChange(undefined)
    }
  }

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (value === '') {
      onChange(undefined)
    } else if (!isNaN(parseInt(value))) {
      onChange(parseInt(value))
    }
  }

  /**
   * Update the input value when the value changes.
   */
  useEffect(() => {
    if (value === undefined) {
      setInputValue('')
    } else {
      setInputValue(value.toString())
    }
  }, [value])

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      options={options}
      value={autocompleteValue}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          label={`${t('fuse')} (A)`}
          {...params}
          {...props}
          // inputProps={{
          //   inputMode: 'numeric'
          // }}
          onChange={handleTextFieldChange}
        />
      )}
    />
  )
}
